export type Oklch = {
  l: number;
  c: number;
  h: number;
};

type Srgb = {
  r: number;
  g: number;
  b: number;
};

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function srgbToOklch({ r, g, b }: Srgb): Oklch {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const c = Math.hypot(a, bb);
  let h = (Math.atan2(bb, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h };
}

function oklchToLinear({ l: L, c, h }: Oklch): [number, number, number] {
  const hr = (h * Math.PI) / 180;
  const a = c * Math.cos(hr);
  const b = c * Math.sin(hr);

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function oklchToSrgb(o: Oklch): Srgb {
  const [lr, lg, lb] = oklchToLinear(o);
  return { r: linearToSrgb(lr), g: linearToSrgb(lg), b: linearToSrgb(lb) };
}

const GAMUT_EPS = 1e-4;

export function inGamut(o: Oklch): boolean {
  const [lr, lg, lb] = oklchToLinear(o);
  return [lr, lg, lb].every((v) => v >= -GAMUT_EPS && v <= 1 + GAMUT_EPS);
}

/** Reduce chroma (binary search) until the color is inside the sRGB gamut. */
export function clampChroma(o: Oklch): Oklch {
  if (inGamut(o)) return o;
  let lo = 0;
  let hi = o.c;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut({ l: o.l, c: mid, h: o.h })) lo = mid;
    else hi = mid;
  }
  return { l: o.l, c: lo, h: o.h };
}

function hslToSrgb(h: number, s: number, l: number): Srgb {
  if (s === 0) return { r: l, g: l, b: l };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return { r: channel(h + 1 / 3), g: channel(h), b: channel(h - 1 / 3) };
}

function parseHex(hex: string): Srgb {
  const s = hex.slice(1);
  const full =
    s.length === 3
      ? s
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : s;
  return {
    r: parseInt(full.slice(0, 2), 16) / 255,
    g: parseInt(full.slice(2, 4), 16) / 255,
    b: parseInt(full.slice(4, 6), 16) / 255,
  };
}

function parseOklchString(css: string): Oklch | null {
  const m = css.match(/oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)/i);
  if (!m) return null;
  const l = m[2] === "%" ? Number(m[1]) / 100 : Number(m[1]);
  return { l, c: Number(m[3]), h: Number(m[4]) };
}

function parseNonOklchToSrgb(css: string): Srgb {
  const s = css.trim();
  if (s.startsWith("#")) return parseHex(s);
  const hsl = s.match(/hsl\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/i);
  if (hsl) return hslToSrgb(Number(hsl[1]) / 360, Number(hsl[2]) / 100, Number(hsl[3]) / 100);
  const rgb = s.match(/rgb\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  if (rgb) return { r: Number(rgb[1]) / 255, g: Number(rgb[2]) / 255, b: Number(rgb[3]) / 255 };
  throw new Error(`Cannot parse color: ${css}`);
}

/** Parse any supported CSS color (oklch/hsl/hex/rgb) to OKLCH. */
export function parseToOklch(css: string): Oklch {
  const oklch = parseOklchString(css);
  return oklch ?? srgbToOklch(parseNonOklchToSrgb(css));
}

/** Parse any supported CSS color to sRGB (0..1), gamut-clamping oklch inputs. */
export function parseToSrgb(css: string): Srgb {
  const oklch = parseOklchString(css);
  if (oklch) {
    const { r, g, b } = oklchToSrgb(clampChroma(oklch));
    return { r: clamp01(r), g: clamp01(g), b: clamp01(b) };
  }
  return parseNonOklchToSrgb(css);
}

export function formatOklch({ l, c, h }: Oklch): string {
  return `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)})`;
}

function relativeLuminance({ r, g, b }: Srgb): number {
  const [lr, lg, lb] = [r, g, b].map((c) => srgbToLinear(clamp01(c)));
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/** WCAG 2.1 contrast ratio between two CSS colors. */
export function contrast(a: string, b: string): number {
  const la = relativeLuminance(parseToSrgb(a));
  const lb = relativeLuminance(parseToSrgb(b));
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}
